import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { PlantHireCategory, ServicePriceType } from "../Services/serviceTypes";
import {
  CreateQuotationStepsEnum,
  STATIC_GROUP_CHILDREN,
  getChildrenForCategory,
} from "./quotationTypes";
import {
  CategoriesSlugWithSummaries,
  CategorySlugs,
} from "./Components/Products/SummaryHelper";
import { ProductComponentTypeEnum } from "../Products/productsTypes";
import { RoleTypesEnum } from "../../../helpers/permissionModule";

// --- TYPES BASED ON BACKEND RESPONSE ---
export interface ServiceCartItem {
  _id: string; // or service_id in some contexts, keeping consistent with UI use
  name: string;
  price_type: ServicePriceType;
  unit_price: number;
  image: string;
  is_hire_service: boolean;
  plant_hire_category?: PlantHireCategory | null;
  qty: number;
  days: number;
  total_price: number | null;
  slug?: string;
  installation_date?: string | null;
  is_edited?: boolean;
  is_date_required: boolean;
  date_label: string;
  frame_qty?: number;
}

export interface SelectedStorageType {
  _id: string;
  category_id: string;
  name: string;
  image: string;
  slug: string;
  product_type_name?: string;
  summary?: any;
}

// Partial interface for the Master Object (Expand as needed based on your full JSON)
export interface QuotationDetails {
  _id: string;
  quote_id?: string; // Sometimes response has quote_id
  customer_id?: any; // Added to support product_for filtering
  status: string;
  is_design: boolean;
  load_notice: any[];
  quote_for?: RoleTypesEnum.DISTRIBUTOR | RoleTypesEnum.CLIENT;

  customer_snapshot?: {
    company_name: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    vat_number: string;
    billing_address: any;
    delivery_address: any;
    preferences: {
      color: string;
      design_preview: boolean;
    };
    show_retail_price: boolean;
    [key: string]: any;
  };

  selected_storage_types?: [
    {
      _id: string;
      name: string;
      image: string;
    },
  ];

  categories_snapshot?: {
    _id: string;
    category_id: string;
    name: string;
    image: string;
    slug: string;
    product_type_name?: string;
    summary?: any;
  }[];

  products?: {
    products: any[];
    accessories: any[];
    additional_info?: any;
    products_total_amount: number;
    accessories_total_amount: number;
  };

  services?: any[];
  services_total?: number;

  bespoke_items?: any[];
  bespoke_items_total?: number;

  payment_milestones?: any[];
  checklist_answers?: any[];
  designs?: any[];
  notes?: {
    internal: any[];
    client: any[];
  };

  grand_total?: number;
  vat_amount?: number;
  discount_amount?: number;
  installation_mark_up_required?: boolean;
  no_products_required?: boolean;
}

// --- GLOBAL STATE STRUCTURE ---
interface QuotationState {
  quotationId: string | null;
  quotationDetails: QuotationDetails | null; // ✅ Single Source of Truth
  loading: boolean;

  // Active Editing State (Drafts for Wizard Steps)
  // We keep these to allow drag-and-drop/editing before saving to server
  cartItems: any[];
  servicesCart: ServiceCartItem[];
  selectedStorageTypes: SelectedStorageType[];
  installationMarkupRequired: boolean;
}

const initialState: QuotationState = {
  quotationId: null,
  quotationDetails: null,
  loading: false,
  cartItems: [],
  servicesCart: [],
  selectedStorageTypes: [],
  installationMarkupRequired: false,
};

const quotationSlice = createSlice({
  name: "quotation",
  initialState,
  reducers: {
    // --- GLOBAL ACTIONS ---
    setSelectedStorageTypes: (
      state,
      action: PayloadAction<SelectedStorageType[]>,
    ) => {
      state.selectedStorageTypes = action.payload;
    },
    setQuotationId: (state, action: PayloadAction<string | null>) => {
      state.quotationId = action.payload;
    },

    // ✅ MAIN ACTION: Updates the full object from API response
    setQuotationDetails: (state, action: PayloadAction<QuotationDetails>) => {
      state.quotationDetails = action.payload;

      // Auto-sync ID
      if (action.payload) {
        state.quotationId = action.payload.quote_id || action.payload._id;
        state.installationMarkupRequired =
          action.payload.installation_mark_up_required || false;
      }
      state.selectedStorageTypes = (
        action.payload.categories_snapshot ?? []
      ).map((item) => ({
        ...item,
        _id: item._id,
        category_id: item.category_id,
        name: item.name,
        image: item.image,
        slug: item.slug,
        product_type_name: item.product_type_name,
        summary: item.summary,
      }));
      // console.log("Redux Categories: ", action?.payload?.categories_snapshot);
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    resetQuotationState: () => initialState,

    setInstallationMarkupRequired: (state, action: PayloadAction<boolean>) => {
      state.installationMarkupRequired = action.payload;
    },

    // --- PRODUCT CART ACTIONS (Active Editing) ---
    setCartItems: (state, action: PayloadAction<any[]>) => {
      state.cartItems = action.payload;
    },
    updateCartItems: (state, action: PayloadAction<any[]>) => {
      action.payload.forEach((item: any) => {
        const index = state.cartItems.findIndex((i) => i._id === item._id);
        if (index !== -1) {
          state.cartItems[index] = item;
        } else {
          state.cartItems.push(item);
        }
      });
    },
    removeCartItem: (state, action: PayloadAction<string>) => {
      state.cartItems = state.cartItems.filter(
        (item) => item._id !== action.payload,
      );
    },

    resetCart: (state) => {
      state.cartItems = [];
    },

    // --- SERVICE CART ACTIONS (Active Editing) ---
    setServicesCart: (state, action: PayloadAction<ServiceCartItem[]>) => {
      state.servicesCart = action.payload;
    },
    addServiceItem: (state, action: PayloadAction<ServiceCartItem>) => {
      const exists = state.servicesCart.find(
        (item) => item._id === action.payload._id,
      );
      if (!exists) {
        state.servicesCart.push(action.payload);
      }
    },
    syncFactoryAssembledServiceQuantity: (state) => {
      const factoryAssembledService = state.servicesCart.find(
        (item) => item.slug === "factory-assembled-frames",
      );
      if (factoryAssembledService && !factoryAssembledService.is_edited) {
        const endFramesProductsQty = state.cartItems
          .filter(
            (item) =>
              item.itemType === "product" &&
              (item.productData?.component_type ===
                ProductComponentTypeEnum.END_FRAME ||
                item.productData?.component_type ===
                  ProductComponentTypeEnum.APR_ENDFRAMES ||
                item.productData?.component_type ===
                  ProductComponentTypeEnum.DRIVE_IN_ENDFRAMES),
          )
          .reduce((acc, item) => acc + (item.qty || 0), 0);

        if (endFramesProductsQty >= factoryAssembledService.qty) {
          factoryAssembledService.qty = endFramesProductsQty;
          // Recalculate price based on price_type
          const { price_type, unit_price, qty, days } = factoryAssembledService;
          if (price_type === ServicePriceType.DAY_PERSON) {
            factoryAssembledService.total_price = unit_price * qty * days;
          } else if (price_type === ServicePriceType.DAY) {
            factoryAssembledService.total_price = unit_price * days * qty;
          } else if (price_type === ServicePriceType.BRACING) {
            factoryAssembledService.total_price = unit_price * qty;
          } else if (price_type === ServicePriceType.PER_QTY) {
            factoryAssembledService.total_price = unit_price * qty;
          }
        }
      }
    },

    updateServiceItem: (
      state,
      action: PayloadAction<{ id: string; changes: Partial<ServiceCartItem> }>,
    ) => {
      const { id, changes } = action.payload;
      const index = state.servicesCart.findIndex((item) => item._id === id);
      if (index !== -1) {
        const updatedItem = { ...state.servicesCart[index], ...changes };

        // Auto-Calculate Price logic
        if (changes.qty !== undefined || changes.days !== undefined) {
          const { price_type, unit_price, qty, days } = updatedItem;
          // Recalculate based on specific pricing logic
          if (price_type === ServicePriceType.DAY_PERSON) {
            updatedItem.total_price = unit_price * qty * days;
          } else if (price_type === ServicePriceType.DAY) {
            updatedItem.total_price = unit_price * days * qty;
          } else if (price_type === ServicePriceType.BRACING) {
            updatedItem.total_price = unit_price * qty;
          } else if (price_type === ServicePriceType.PER_QTY) {
            updatedItem.total_price = unit_price * qty;
          }
          // Note: "Custom" price types usually update total_price directly via changes
        }
        state.servicesCart[index] = updatedItem;
      }
    },
    removeServiceItem: (state, action: PayloadAction<string>) => {
      state.servicesCart = state.servicesCart.filter(
        (item) => item._id !== action.payload,
      );
    },
    resetServicesCart: (state) => {
      state.servicesCart = [];
    },
    updateLoadNotices: (state, action: PayloadAction<any[]>) => {
      if (state.quotationDetails && action.payload) {
        state.quotationDetails.load_notice = action.payload;
      }
    },
  },
});

export const {
  setQuotationId,
  setQuotationDetails,
  setLoading,
  resetQuotationState,
  setInstallationMarkupRequired,

  // Products
  setCartItems,
  updateCartItems,
  removeCartItem,
  resetCart,

  // Services
  setServicesCart,
  addServiceItem,
  syncFactoryAssembledServiceQuantity,
  updateServiceItem,
  removeServiceItem,
  resetServicesCart,
  setSelectedStorageTypes,
  updateLoadNotices,
} = quotationSlice.actions;

// --- SELECTORS ---

// Base Selectors
export const selectShowRetailPrice = (state: any) =>
  state.quotation?.quotationDetails?.customer_snapshot?.show_retail_price ??
  state.quotation?.quotationDetails?.show_retail_price ??
  state.quoteRequest?.quoteRequestDetails?.customer_snapshot?.show_retail_price ??
  state.quoteRequest?.quoteRequestDetails?.show_retail_price ??
  true;
export const selectQuoteFor = (state: any) =>
  state.quotation.quotationDetails?.customer_snapshot?.quote_for?.key ??
  RoleTypesEnum.CLIENT;
export const selectSelectedStorageTypes = (state: any) =>
  state.quotation.selectedStorageTypes;
export const selectQuotationId = (state: any) => state.quotation.quotationId;
export const selectQuotationDetails = (state: any) =>
  state.quotation.quotationDetails;
export const selectQuotationLoading = (state: any) => state.quotation.loading;
export const isAPRCategorySelected = (state: any) =>
  state.quotation.selectedStorageTypes?.some(
    (item: any) => item?.slug === "adjustable-pallet-racking-apr",
  );
export const selectInstallationMarkupRequired = (state: any) =>
  state.quotation.installationMarkupRequired;

// Derived Data Selectors (Replacing 'Shared' State)
export const selectCustomerSnapshot = (state: any) =>
  state.quotation?.quotationDetails?.customer_snapshot ||
  state.quoteRequest?.quoteRequestDetails?.customer_snapshot ||
  state.quoteRequest?.quoteRequestDetails?.selected_change?.customer_snapshot;

export const selectCategorySnapshot = (state: any) =>
  state.quotation.quotationDetails?.categories_snapshot; // Replaces shared.selectedCategory

export const selectAdditionalInfo = (state: any) =>
  state.quotation.quotationDetails?.products?.additional_info; // Replaces shared.savedInfoOptions

export const selectDesignFlag = (state: any) =>
  state.quotation.quotationDetails?.is_design;

export const selectCategorySummary = (state: any, item_id: string) =>
  state.quotation.quotationDetails?.products?.find(
    (item: any) => item._id === item_id,
  )?.summery;

export const selectLastCompletedStep = (state: any) =>
  state.quotation.quotationDetails?.last_completed_step ??
  CreateQuotationStepsEnum.CUSTOMER_DETAILS;

// Cart Selectors
export const selectCartItems = (state: any) => state.quotation.cartItems;
export const selectSectionCartItems = createSelector(
  [selectCartItems, (state: any, sectionId: string) => sectionId],
  (cartItems, sectionId) =>
    cartItems.filter((item: any) => item.item_id === sectionId),
);

export const selectEndFramesProductsQty = (state: any) =>
  state.quotation.cartItems
    .filter(
      (item: any) =>
        item.itemType === "product" &&
        (item.productData?.component_type ===
          ProductComponentTypeEnum.END_FRAME ||
          item.productData?.component_type ===
            ProductComponentTypeEnum.APR_ENDFRAMES ||
          item.productData?.component_type ===
            ProductComponentTypeEnum.DRIVE_IN_ENDFRAMES),
    )
    .reduce((acc: number, item: any) => acc + (item.qty || 0), 0);

export const selectServicesCart = (state: any) => state.quotation.servicesCart;
export const selectServicesTotal = (state: any) =>
  state.quotation.servicesCart.reduce(
    (sum: number, item: ServiceCartItem) => sum + (item.total_price || 0),
    0,
  );

// --- NAVIGATION PLAYLIST LOGIC ---
export const calculateStepPlaylist = (
  selectedStorageTypes: SelectedStorageType[],
  isInstallationRequired: boolean
): string[] => {
  const playlist: string[] = [
    CreateQuotationStepsEnum.CUSTOMER_DETAILS,
    CreateQuotationStepsEnum.STORAGE_TYPES,
  ];

  selectedStorageTypes.forEach((cat) => {
    const children = getChildrenForCategory(cat);
    children.forEach((child) => {
      if (child.key === CreateQuotationStepsEnum.SUMMARY) {
        if (!CategoriesSlugWithSummaries.includes(cat.slug as CategorySlugs)) {
          return;
        }
      }
      playlist.push(`${cat._id}__${child.key}`);
    });
  });

  playlist.push(
    CreateQuotationStepsEnum.BESPOKE_ITEMS,
    CreateQuotationStepsEnum.SERVICES,
    CreateQuotationStepsEnum.LOAD_NOTICES,
  );

  // if (isInstallationRequired) {
  //   playlist.push(CreateQuotationStepsEnum.INSTALLATION_CHECKLIST);
  // }

  playlist.push(
    CreateQuotationStepsEnum.COLLECTION_DELIVERY,
    CreateQuotationStepsEnum.NOTES,
    CreateQuotationStepsEnum.DESIGNS,
    CreateQuotationStepsEnum.REVIEW,
  );

  return playlist;
};

export const selectStepPlaylist = createSelector(
  [selectSelectedStorageTypes, selectInstallationMarkupRequired],
  (selectedStorageTypes: SelectedStorageType[], isInstallationRequired: boolean) =>
    calculateStepPlaylist(selectedStorageTypes, isInstallationRequired),
);

export const findStepIndexInPlaylist = (playlist: string[], step: string | null | undefined): number => {
  if (!step || !playlist || playlist.length === 0) return -1;

  // 1. Exact match
  const exactIndex = playlist.indexOf(step);
  if (exactIndex !== -1) return exactIndex;

  // 2. Map step key aliases & normalize underscores to hyphens
  const cleanStep = step.toLowerCase().replace(/_/g, "-");
  const normalizedStep =
    cleanStep === "mezzanine-floor"
      ? "floor-details"
      : cleanStep === "storage-type"
      ? "storage-types"
      : cleanStep;

  const normalizedExactIndex = playlist.indexOf(normalizedStep);
  if (normalizedExactIndex !== -1) return normalizedExactIndex;

  // 3. Match category-prefixed step keys in playlist (e.g. "catId__floor-details" or "catId__products")
  const suffixIndex = playlist.findIndex((item) => {
    const cleanItem = item.toLowerCase().replace(/_/g, "-");
    if (cleanItem === normalizedStep || item === step) return true;
    if (cleanItem.endsWith(`__${normalizedStep}`) || item.endsWith(`__${step}`)) return true;
    const itemKey = cleanItem.includes("__") ? cleanItem.split("__")[1] : cleanItem;
    return itemKey === normalizedStep || itemKey === step;
  });
  if (suffixIndex !== -1) return suffixIndex;

  // 4. Extract sub-step if step itself is category-prefixed
  const subStep = step.includes("__") ? step.split("__")[1] : step;
  const cleanSubStep = subStep.toLowerCase().replace(/_/g, "-");
  const normalizedSubStep =
    cleanSubStep === "mezzanine-floor" ? "floor-details" : cleanSubStep;

  return playlist.findIndex((item) => {
    const cleanItem = item.toLowerCase().replace(/_/g, "-");
    const itemSubStep = cleanItem.includes("__") ? cleanItem.split("__")[1] : cleanItem;
    return itemSubStep === normalizedSubStep || itemSubStep === subStep;
  });
};

export const hasAddress = (address: any): boolean => {
  if (!address) return false;
  if (typeof address === "string") {
    return address.trim().length > 0;
  }
  if (Array.isArray(address)) {
    return address.length > 0 && address.some((item) => hasAddress(item));
  }
  if (typeof address === "object") {
    const keys = Object.keys(address);
    if (keys.length === 0) return false;
    return keys.some((k) => {
      const val = address[k];
      if (val === null || val === undefined) return false;
      if (typeof val === "boolean" || typeof val === "number") return true;
      if (typeof val === "string") return val.trim().length > 0;
      if (typeof val === "object") return Object.keys(val).length > 0;
      return true;
    });
  }
  return true;
};

export const hasQuotationAddresses = (
  quotationDetails?: QuotationDetails | null
): boolean => {
  if (!quotationDetails) return false;
  const snapshot = quotationDetails.customer_snapshot;

  const hasBilling =
    hasAddress(snapshot?.billing_address) ||
    hasAddress(snapshot?.billing_address_id) ||
    hasAddress((quotationDetails as any)?.billing_address) ||
    hasAddress((quotationDetails as any)?.billing_address_id);

  const hasDelivery =
    hasAddress(snapshot?.delivery_address) ||
    hasAddress(snapshot?.delivery_address_id) ||
    hasAddress((quotationDetails as any)?.delivery_address) ||
    hasAddress((quotationDetails as any)?.delivery_address_id) ||
    hasAddress((quotationDetails as any)?.delivery);

  return hasBilling && hasDelivery;
};

export const isStepDisabledInPlaylist = (
  playlist: string[],
  currentStep: string,
  lastCompletedStep: string,
  quotationDetails?: QuotationDetails | null
): boolean => {
  const isCustomerDetailsStep =
    currentStep === CreateQuotationStepsEnum.CUSTOMER_DETAILS ||
    currentStep === "customer_details" ||
    currentStep === "customer-details" ||
    currentStep.endsWith(`__${CreateQuotationStepsEnum.CUSTOMER_DETAILS}`);

  // First condition for all tabs and sub-tabs (except Customer Details):
  // Any tab/sub-tab other than Customer details is disabled unless Delivery Address and Billing Address are present.
  if (!isCustomerDetailsStep) {
    if (!hasQuotationAddresses(quotationDetails)) {
      return true;
    }
  }

  const currentStepIndex = findStepIndexInPlaylist(playlist, currentStep);
  const lastCompletedStepIndex = findStepIndexInPlaylist(playlist, lastCompletedStep);

  // If current step or last completed step is unmapped, default to enabled
  if (currentStepIndex === -1 || lastCompletedStepIndex === -1) return false;

  // The step immediately following lastCompletedStep is enabled (unlocked to be completed next).
  return currentStepIndex > lastCompletedStepIndex + 1;
};

export default quotationSlice.reducer;
